/**
 * CRO single-column auth card · matches
 * docs/design-preview/option-b/wave-1/01c-login-cro.html.
 *
 * 3-state flow:
 *   step 'email'    · email input + "Continue with email"
 *                     on submit we call /api/v1/auth/email-exists
 *   step 'login'    · email shown read-only + password
 *                     (chosen when the backend says the email exists)
 *   step 'register' · email shown read-only + Full name + password
 *                     (chosen when the backend says it doesn't exist)
 *
 * Both `login` and `register` resolve to the dashboard on success.
 * The Cognito pool auto-confirms via PreSignUp Lambda, so register
 * also lands the user in one round-trip.
 */
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button/button';
import { Input } from '@/components/ui/input/input';
import { capture } from '@/lib/analytics';

import { checkEmailExists, signIn, signInWithSocial, signUp } from '../api/amplify-auth';
import { mapAuthError } from '../api/errors';
import { SignupSchema } from '../schemas';

import styles from './auth-form-shell.module.css';

import type { SocialProvider } from '../types';

interface AuthFormShellProps {
  /** Called after a successful sign-in or sign-up. */
  onSignedIn: () => void;
}

type Step = 'email' | 'login' | 'register';

function GoogleGlyph() {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true" className={styles.socialIcon}>
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.3-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14l6.6 4.8C14.7 15 19 12 24 12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3c-2 1.4-4.6 2.5-7.4 2.5-5.3 0-9.7-3.3-11.3-8L6.1 32C9.5 38.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.6l6.3 5.3C41.4 35.4 44 30 44 24c0-1.3-.1-2.3-.4-3.5z"
      />
    </svg>
  );
}

function MetaGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.socialIcon}>
      <path
        fill="#1877F2"
        d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
      />
    </svg>
  );
}

export function AuthFormShell({ onSignedIn }: AuthFormShellProps) {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pendingSocial, setPendingSocial] = useState<SocialProvider | null>(null);

  // Focus the first field of whichever step is showing. Done with a
  // ref + effect rather than the `autoFocus` prop: `autoFocus` fires
  // only on initial mount (it would not move focus when the card
  // transitions email → login/register) and trips the a11y lint rule.
  const firstFieldRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    firstFieldRef.current?.focus();
  }, [step]);

  const onEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const trimmed = email.trim();
    const parsed = SignupSchema.pick({ email: true }).safeParse({ email: trimmed });
    if (!parsed.success) {
      setEmailError(parsed.error.issues[0]?.message ?? 'Enter a valid email');
      return;
    }
    setEmail(trimmed);
    setEmailError(null);
    setBusy(true);
    const exists = await checkEmailExists(trimmed);
    setBusy(false);
    setStep(exists ? 'login' : 'register');
  };

  const onLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setPasswordError(null);
    if (password.length < 1) {
      setPasswordError('Password is required');
      return;
    }
    setBusy(true);
    try {
      await signIn({ email, password });
      onSignedIn();
    } catch (err) {
      setFormError(mapAuthError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const onRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setNameError(null);
    setPasswordError(null);

    const trimmedName = name.trim();
    if (trimmedName.length < 2) {
      setNameError('Enter your full name');
      return;
    }

    const parsed = SignupSchema.safeParse({ email, password });
    if (!parsed.success) {
      const msg = parsed.error.issues.find((i) => i.path[0] === 'password')?.message;
      setPasswordError(msg ?? 'Password does not meet requirements');
      return;
    }

    setBusy(true);
    try {
      await signUp({ email, password, name: trimmedName });
      capture('signup_completed');
      onSignedIn();
    } catch (err) {
      const mapped = mapAuthError(err);
      if (mapped.code === 'UserAlreadyExists') {
        // Cognito knows this email but the local /email-exists check returned false
        // (local DB row is missing, or this is a fresh local env). Switch to the
        // LOGIN step with email + password preserved so the user can sign in
        // without re-typing. The signIn path lazily creates the local user row.
        setStep('login');
        setFormError('Looks like you already have an account. Sign in below.');
      } else {
        setFormError(mapped.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const onSocial = async (provider: SocialProvider) => {
    setPendingSocial(provider);
    setFormError(null);
    try {
      await signInWithSocial(provider);
    } catch (err) {
      setFormError(mapAuthError(err).message);
      setPendingSocial(null);
    }
  };

  const useDifferentEmail = () => {
    setStep('email');
    setPassword('');
    setName('');
    setNameError(null);
    setPasswordError(null);
    setFormError(null);
  };

  return (
    <section className={styles.card}>
      <h1 className={styles.headline}>
        Make money online <span className={styles.textGrad}>without showing your face.</span>
      </h1>
      <p className={styles.sub}>
        Build your AI avatar in 5 minutes. Ship your first reel today. Free for 7 days.
      </p>

      {/* Social-first stack · Google primary white, Meta secondary dark */}
      <div className={styles.authStack}>
        <button
          type="button"
          className={styles.socialPrimary}
          onClick={() => void onSocial('Google')}
          disabled={pendingSocial !== null || busy}
        >
          <GoogleGlyph />
          {pendingSocial === 'Google' ? 'Connecting…' : 'Continue with Google'}
        </button>
        <button
          type="button"
          className={styles.socialSecondary}
          onClick={() => void onSocial('Facebook')}
          disabled={pendingSocial !== null || busy}
        >
          <MetaGlyph />
          {pendingSocial === 'Facebook' ? 'Connecting…' : 'Continue with Meta'}
        </button>
      </div>

      <div className={styles.orDivider}>OR</div>

      {step === 'email' ? (
        <form className={styles.magicForm} onSubmit={(e) => void onEmailContinue(e)} noValidate>
          <Input
            label="Email"
            hideLabel
            type="email"
            autoComplete="email"
            placeholder="you@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={emailError ?? undefined}
            aria-invalid={emailError ? true : undefined}
            ref={firstFieldRef}
          />
          <Button type="submit" variant="primary" size="lg" fullWidth isLoading={busy}>
            Continue with email →
          </Button>
          <p className={styles.formHint}>
            New here? We&apos;ll create your account. Already a user? You&apos;ll just enter your
            password.
          </p>
        </form>
      ) : null}

      {step === 'login' ? (
        <form className={styles.magicForm} onSubmit={(e) => void onLoginSubmit(e)} noValidate>
          <div className={styles.emailRow}>
            <span className={styles.emailRowLabel}>Email</span>
            <span className={styles.emailRowValue}>{email}</span>
            <button type="button" className={styles.linkBtn} onClick={useDifferentEmail}>
              Use a different email
            </button>
          </div>
          <Input
            label="Password"
            hideLabel
            type="password"
            autoComplete="current-password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={passwordError ?? undefined}
            aria-invalid={passwordError ? true : undefined}
            ref={firstFieldRef}
          />
          {formError ? (
            <p className={styles.formError} role="alert">
              {formError}
            </p>
          ) : null}
          <Button type="submit" variant="primary" size="lg" fullWidth isLoading={busy}>
            Sign in →
          </Button>
        </form>
      ) : null}

      {step === 'register' ? (
        <form className={styles.magicForm} onSubmit={(e) => void onRegisterSubmit(e)} noValidate>
          <div className={styles.emailRow}>
            <span className={styles.emailRowLabel}>Email</span>
            <span className={styles.emailRowValue}>{email}</span>
            <button type="button" className={styles.linkBtn} onClick={useDifferentEmail}>
              Use a different email
            </button>
          </div>
          <Input
            label="Full name"
            hideLabel
            type="text"
            autoComplete="name"
            placeholder="Your full name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={nameError ?? undefined}
            aria-invalid={nameError ? true : undefined}
            ref={firstFieldRef}
          />
          <Input
            label="Password"
            hideLabel
            type="password"
            autoComplete="new-password"
            placeholder="Create a password (at least 6 characters)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={passwordError ?? undefined}
            aria-invalid={passwordError ? true : undefined}
          />
          {formError ? (
            <p className={styles.formError} role="alert">
              {formError}
            </p>
          ) : null}
          <Button type="submit" variant="primary" size="lg" fullWidth isLoading={busy}>
            Create account →
          </Button>
          <p className={styles.formHint}>We won&apos;t post anything without your permission.</p>
        </form>
      ) : null}

      {/* Trust halo · Trustpilot stars sit within ~100px of the CTA */}
      <div className={styles.trustHalo}>
        <span className={styles.trustBrand}>Trustpilot</span>
        <span className={styles.trustStars} aria-hidden="true">
          <span className={styles.star} />
          <span className={styles.star} />
          <span className={styles.star} />
          <span className={styles.star} />
          <span className={styles.star} />
        </span>
        <span className={styles.trustRating}>4.8</span>
        <span className={styles.trustMeta}>· 2,341 reviews</span>
      </div>

      <p className={styles.micro}>
        No credit card required · Cancel anytime · We never post without permission
        <br />
        By continuing you agree to our <a href="/terms">Terms</a> and <a href="/privacy">Privacy</a>
        .
      </p>

      {/* Testimonial · real handle, specific outcome numbers */}
      <div className={styles.proof}>
        <div className={styles.proofPic}>
          <img
            src="https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=200&q=80&auto=format&fit=crop"
            alt=""
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
        <div className={styles.proofText}>
          <div className={styles.proofQuote}>
            &quot;Built one avatar. Ran it 30 days. Quit the warehouse.&quot;
          </div>
          {/* Mirrors the design's div.meta: handle + numbers are child
              elements, the connective text sits in direct text nodes so
              the parity differ reads the same own-text string. */}
          <div className={styles.proofMeta}>
            <strong>@marcus_ships</strong> · <span className={styles.proofNum}>$1,847</span> in 30
            days · <span className={styles.proofNum}>47</span> reels shipped
          </div>
        </div>
      </div>
    </section>
  );
}
